import {booleanAttribute, ChangeDetectionStrategy, Component, effect, Injectable, input, output} from "@angular/core";
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MøtedeltakereModell, MøtedeltakerModell, MøteId, StemmeberettigetMøtedeltakerSpesifikasjoner, StorStreng} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TranslateModule} from "@ngx-translate/core";
import {MatTooltip} from "@angular/material/tooltip";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {MatButton} from "@angular/material/button";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {totaltAntallAksjerForMøtedeltaker} from "felles-ng2/modell/møtedeltakerModellHjelpere";
import {InlineSkjemaKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema";
import {InlineSkjemaHandlingerKomponent} from "web-ng2/delt/komponenter/inline-skjema/inline-skjema-handlinger/inline-skjema-handlinger";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {toSignal} from "@angular/core/rxjs-interop";
import {map} from "rxjs/operators";
import {AvstemningDatatjeneste} from "felles-ng2/tjenester/datalager/AvstemningDatatjeneste";

const debug = initDebugger('AvstemningRedigerStemmeberettigedeKomponent');

export type StemmeberettigedeSkjemaType = ReturnType<typeof AvstemningRedigerStemmeberettigedeKomponent.opprettSkjema>
export type StemmeberettigetSkjemaType = ReturnType<typeof AvstemningRedigerStemmeberettigedeKomponent.opprettSkjemarad>
export type StemmeberettigetSkjemaVerdier = StemmeberettigetSkjemaType['value'];

//@todo ved red når ingen er valgt og GF - marker alle som har beholdning som valgt?
//@todo deltakere som har 0 behold får 1 i stemmevekt? ref no.systemfabrikken.lederplan.domene.møte.Møtedeltaker.Stemmegivning.stemmevekt
@Component({
    selector: 'avstemning-rediger-stemmeberettigede',
    templateUrl: 'avstemning-rediger-stemmeberettigede.html',
    styleUrls: ['avstemning-rediger-stemmeberettigede.scss'],
    imports: [
        FormsModule,
        MatInputModule,
        ReactiveFormsModule,
        MatCheckboxModule,
        MatTooltip,
        MatButton,
        TranslateModule,
        NavigasjonforhindretFlashDirektiv,
        VisSpinnerDirektiv,
        InlineSkjemaKomponent,
        InlineSkjemaHandlingerKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvstemningRedigerStemmeberettigedeKomponent {
    static ajsSelector = 'moeteRedigerStemmeberettigede';

    static opprettSkjema() {
        return new FormArray<StemmeberettigetSkjemaType>([]);
    }

    static opprettSkjemarad(deltaker: MøtedeltakerModell) {
        return new FormGroup({
            deltaker: new FormControl<MøtedeltakerModell>(deltaker, {nonNullable: true}),
            antallAksjerTotalt: new FormControl<number>(totaltAntallAksjerForMøtedeltaker(deltaker), {nonNullable: true}),
            erStemmeberettiget: new FormControl<boolean>(deltaker.erStemmeberettiget, {nonNullable: true}),
            stemmevekt: new FormControl<number>(deltaker.stemmevekt, {nonNullable: true}), // se skjemaradValidator for validering
            stemmekommentar: new FormControl<StorStreng>(deltaker.stemmekommentar, {nonNullable: true, validators: FeltValidatorer.litenStreng})
        }, {validators: kontroll => AvstemningRedigerStemmeberettigedeKomponent.skjemaradValidator(kontroll)});
    }

    //kan ikke types som StemmeberettigedeSkjemaType pga sirkulær avhengighet
    static skjemaradValidator(rad: any): ValidationErrors | null {
        if (rad.value.erStemmeberettiget && (erNil(rad.value.stemmevekt) || rad.value.stemmevekt <= 0))
            rad.controls.stemmevekt.setErrors({'stemmevekt_mangler': true});
        else
            rad.controls.stemmevekt.setErrors(null);
        return null;
    }

    /** kalles med oppdaterte møtedeltakere når endringer har blitt lagret, null dersom bruker avbrøt */
    vedLukk = output<MøtedeltakereModell | null>();

    moeteId = input.required<MøteId>();
    moetedeltakere = input.required<MøtedeltakerModell[]>();
    visAntallAksjer = input(false, {transform: booleanAttribute});

    skjema = new FormArray<StemmeberettigetSkjemaType>([]);
    alleValgt = toSignal(this.skjema.valueChanges.pipe(map(rader =>
      rader.every(rad => rad.erStemmeberettiget))), {initialValue: false});
    ingenValgt = toSignal(this.skjema.valueChanges.pipe(map(rader =>
      rader.every(rad => !rad.erStemmeberettiget))), {initialValue: false});

    lagrer = asynkArbeidPågårSignal();

    constructor(private avhengigheter: AvstemningRedigerStemmeberettigedeKomponentAvhengigheter) {
        effect(() => {
            this.skjema.clear();
            this.moetedeltakere().forEach(deltaker => this.skjema.push(AvstemningRedigerStemmeberettigedeKomponent.opprettSkjemarad(deltaker)));
        });
    }

    endreAlle() {
        const erValgt = !this.alleValgt();
        this.skjema.controls.forEach(kontroll => kontroll.controls.erStemmeberettiget.setValue(erValgt));
    }

    setStemmevektTilAntallAksjerForAlle() {
        this.skjema.patchValue(this.skjema.value.map(rad => ({stemmevekt: rad.antallAksjerTotalt})));
        //markerer som touched slik at eventuelle valideringsfeil blir vist
        this.skjema.controls.forEach(rad => rad.controls.stemmevekt.markAsTouched());
    }

    setStemmevektTilAntallAksjer(rad: StemmeberettigetSkjemaType) {
        rad.patchValue({stemmevekt: rad.value.antallAksjerTotalt});
        rad.controls.stemmevekt.markAsTouched();
    }

    async lagre() {
        await this.lagrer.utfør(() =>
          this.avhengigheter.lagre(this.moeteId(), this.somSpesifikasjon()));
        this.vedLukk.emit(null);
    }

    avbryt() {
        this.vedLukk.emit(null);
    }

    // private hjelpemetoder

    private somSpesifikasjon(): StemmeberettigetMøtedeltakerSpesifikasjoner {
        return {
            deltakere: this.skjema.value.map(deltaker => ({
                deltakerId: deltaker.deltaker!.id,
                erStemmeberettiget: deltaker.erStemmeberettiget!,
                stemmevekt: deltaker.stemmevekt!,
                stemmekommentar: deltaker.stemmekommentar!
            })!)
        }
    }
}

@Injectable()
export class AvstemningRedigerStemmeberettigedeKomponentAvhengigheter {

    constructor(private avstemningDatatjeneste: AvstemningDatatjeneste) {
    }

    async lagre(møteId: MøteId, spesifikasjon: StemmeberettigetMøtedeltakerSpesifikasjoner): Promise<void> {
        await this.avstemningDatatjeneste.oppdaterStemmeberettigede(møteId, spesifikasjon);
    }

}
